// ─────────────────────────────────────────────────────────────────────────────
// ArtistProfileModal, CD's artist (band) profile (artist-profile-modal.html),
// ported 1:1. Two columns: a dark hero rail (band photo, name, location + genre,
// Message, Friends strip) and a content column (Members → chevron opens each
// person's profile, Upcoming shows).
//
// TWO MODES:
//   • Own band, driven by props (bandName, photoUrl, members, friends, genre,
//     location); upcoming shows fetched via fetchUpcomingShows. Opened from a
//     person modal's affiliation strip.
//   • Friend band, pass a `slug`; the whole view-model (members, friends,
//     genres, location, photo, shows) is fetched via fetchBandView(slug)
//     (/api/bands/:slug/roster + /friends + /shows). Opened from a Top 8 tile or
//     a Friends row. A member chevron opens that person's gated profile.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState, type JSX } from 'react';
import { createPortal } from 'react-dom';
import { gradientFor, fetchUpcomingShows, type UpcomingShow } from '../../lib';
import { fetchBandView, bandSlug, type BandShowRow, type BandView, type CrewMember, type Friend } from '../../lib/contacts';
import './profile-modal.css';

const AC_POLY =
  '269 144.98 317.48 96.5 317.48 0 207.22 0 158.74 48.48 110.26 0 0 0 0 110.26 48.48 158.74 48.48 158.74 48.48 158.74 0 207.22 0 317.48 110.26 317.48 158.74 269 207.22 317.48 317.48 317.48 317.48 220.99 269 172.5 269 172.19 195.65 172.19 195.65 195.34 158.9 195.34 122.14 195.34 122.14 158.58 122.14 121.83 158.9 121.83 195.65 121.83 195.65 144.98 269 144.98';
const AcMark = (): JSX.Element => (<svg viewBox="0 0 317.48 317.48" aria-hidden="true"><polygon points={AC_POLY} /></svg>);
const Chevron = (): JSX.Element => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>);

function roleText(role: string): string {
  const r = String(role || '').toLowerCase();
  if (r === 'band_member_av') return 'Band Member';
  if (!r) return 'Member';
  return r.replace(/_/g, ' ').replace(/\bav\b/i, '').trim().replace(/\b\w/g, (c) => c.toUpperCase());
}
function isAv(role: string): boolean { return /band_member_av|_av$/i.test(String(role || '')); }

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function dateParts(iso: string): { mo: string; day: string } {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  if (!m) return { mo: '', day: '' };
  return { mo: MONTHS[Math.max(0, Math.min(11, Number(m[2]) - 1))], day: m[3] };
}

/** Normalize an own-band UpcomingShow into the same row shape friend bands use. */
function ownShowRow(s: UpcomingShow): BandShowRow {
  const ev = s.raw as { venue?: string; city?: string };
  const venue = ev.venue || s.title || s.subtitle || s.kind;
  const loc = ev.city || (s.subtitle !== venue ? s.subtitle : '');
  return { id: s.id, date: s.date, venue, loc, fest: /fest/i.test(s.kind) || /fest/i.test(venue) };
}

export interface ArtistProfileModalProps {
  /** Friend-band mode: when set, the whole view-model is fetched by slug. */
  readonly slug?: string;
  // Own-band props (used when slug is absent):
  readonly bandName?: string;
  readonly photoUrl?: string;
  readonly members?: ReadonlyArray<CrewMember>;
  readonly friends?: ReadonlyArray<Friend>;
  readonly location?: string;
  readonly genre?: string;
  readonly onClose: () => void;
  readonly onBack: () => void;
  readonly onOpenMember: (member: CrewMember) => void;
  /** Open another band (a friend tile) by slug, swaps this modal to that band. */
  readonly onOpenBand?: (slug: string) => void;
  /** Self-view: the band viewing its OWN profile (band-primary "View my
   *  profile"). Hides friend-only affordances, the Message button (can't
   *  message yourself) and the Back arrow (opened standalone, no nav stack). */
  readonly selfView?: boolean;
}

export function ArtistProfileModal(props: ArtistProfileModalProps): JSX.Element {
  const { slug, onClose, onBack, onOpenMember, onOpenBand, selfView } = props;

  // Friend-band view-model (only when slug is set).
  const [view, setView] = useState<BandView | null>(null);
  useEffect(() => {
    if (!slug) { setView(null); return; }
    let live = true;
    setView(null);
    fetchBandView(slug).then((v) => { if (live) setView(v); }).catch(() => {});
    return () => { live = false; };
  }, [slug]);

  // Own-band upcoming shows (only when no slug).
  const [ownShows, setOwnShows] = useState<UpcomingShow[] | null>(null);
  useEffect(() => {
    if (slug) return;
    let live = true;
    fetchUpcomingShows(6).then((rows) => { if (live) setOwnShows(rows); }).catch(() => { if (live) setOwnShows([]); });
    return () => { live = false; };
  }, [slug]);

  // Unified view, friend-band fields win when a slug was given.
  const name = slug ? (view?.name || props.bandName || '') : (props.bandName || '');
  const photoUrl = slug ? (view?.photoUrl || '') : (props.photoUrl || '');
  const members: ReadonlyArray<CrewMember> = slug ? (view?.members || []) : (props.members || []);
  const friends: ReadonlyArray<Friend> = slug ? (view?.friends || []) : (props.friends || []);
  const location = slug ? (view?.location || '') : (props.location || '');
  const genre = slug ? ((view?.genres || []).join(' · ')) : (props.genre || '');
  const shows: BandShowRow[] | null = slug
    ? (view ? view.shows : null)
    : (ownShows ? ownShows.map(ownShowRow) : null);

  const g = gradientFor(name);
  const photoStyle = photoUrl
    ? { backgroundImage: `url('${photoUrl.replace(/'/g, "\\'")}')` }
    : { backgroundImage: `linear-gradient(150deg, ${g.from}, ${g.to})` };

  // Portal to <body> so position:fixed is relative to the viewport, not a
  // transformed ancestor (e.g. the nav header when opened from the BandSwitcher),
  // which would trap the overlay to a sliver at the top. Same fix as MyProfileModal.
  return createPortal(
    <div className="profile-overlay">
      <div className="scrim" onClick={onClose} />
      <article className="profile profile--artist" data-screen-label="Artist profile">
        <div className="pnav">
          {!selfView && <button type="button" aria-label="Back" onClick={onBack}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 6l-6 6 6 6" /></svg></button>}
          <button type="button" aria-label="Close" onClick={onClose}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg></button>
        </div>

        {/* LEFT HERO */}
        <aside className="rail">
          <div className="rail__photo" role="img" aria-label={name} style={photoStyle}>
            <div className="rail__id">
              <h1 className="rail__name">{name || 'Artist'}</h1>
              {location && <p className="rail__loc">{location}</p>}
              {genre && <p className="rail__genre">{genre}</p>}
            </div>
          </div>
          <div className="rail__body">
            {/* pp-messaging 2026-07-20 (Ryan): band-to-band messaging retired.
                Message individual people (crew/members), not the band. */}
            {friends.length > 0 && (
              <>
                <div className="rail__friends-h"><p className="eyebrow">Friends</p></div>
                <div className="friends">
                  {friends.map((f) => {
                    const fg = gradientFor(f.name);
                    const art = f.avatarUrl
                      ? { backgroundImage: `url('${f.avatarUrl.replace(/'/g, "\\'")}')` }
                      : { backgroundImage: `linear-gradient(150deg, ${fg.from}, ${fg.to})` };
                    return (
                      <button className="friend" type="button" key={f.id} onClick={() => onOpenBand?.(bandSlug(f.name) || f.id)} style={onOpenBand ? { cursor: 'pointer' } : undefined}>
                        <span className="friend__art" style={art} />
                        <span className="friend__n">{f.name}</span>
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </aside>

        {/* RIGHT CONTENT */}
        <div className="content">
          <section className="csec">
            <p className="eyebrow csec__h">Members</p>
            <div className="members">
              {slug && !view && <p className="pb__bio" style={{ color: 'var(--ac-color-t3)' }}>Loading…</p>}
              {(!slug || view) && members.length === 0 && <p className="pb__bio" style={{ color: 'var(--ac-color-t3)' }}>No members yet.</p>}
              {members.map((m) => {
                const mg = gradientFor(m.name);
                const av = m.avatarUrl
                  ? { backgroundImage: `url('${m.avatarUrl.replace(/'/g, "\\'")}')` }
                  : { backgroundImage: `linear-gradient(140deg, ${mg.from}, ${mg.to})` };
                return (
                  <a className="member" href="#" key={m.id} onClick={(e) => { e.preventDefault(); onOpenMember(m); }}>
                    <span className="member__av" style={av} />
                    <div className="member__b"><p className="member__n">{m.name}</p></div>
                    <span className="member__r">{roleText(m.role)}{isAv(m.role) && <span className="member__x"><AcMark /></span>}</span>
                    <span className="member__chev"><Chevron /></span>
                  </a>
                );
              })}
            </div>
          </section>

          {shows && shows.length > 0 && (
            <section className="csec csec--bordered">
              <p className="eyebrow csec__h">Upcoming shows</p>
              <div className="shows">
                {shows.map((s) => {
                  const d = dateParts(s.date);
                  // Display-only (legacy has no click action on a friend band's
                  // public show rows), a div, not a dead link.
                  return (
                    <div className="show" key={s.id}>
                      <span className="show__date"><span className="show__mo">{d.mo}</span><span className="show__day">{d.day}</span></span>
                      <div className="show__b">
                        <span className="show__v">{s.venue}</span>
                        {s.loc && <span className="show__loc">{s.loc}</span>}
                        {s.fest && <span className="fest">Fest</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      </article>
    </div>,
    document.body,
  );
}
